import React, { useEffect, useRef, useState } from 'react'
import GraphQLErrorList from '../../components/graphql-error-list'
import SEO from '../../components/seo'
import Layout from '../../containers/layout'
import Container from '../../components/container'
import CloudIndexNav from '../../components/cloudIndexNav/cloudIndexNav'
import { useWindowSize } from '../../hooks/useWindowSize.hook'
import * as styles from './infra.module.css'
import linkStyle from '../../styles/components/links.module.css'
import { graphql, Link } from 'gatsby'
import Icon from '../../components/icons'
import InfraredReportHero from '../../components/infraredReportHero/infraredReportHero'
import chart1Mp4 from '../../img/infrared/charts/chart-1_animation.mp4'
import chart2Mp4 from '../../img/infrared/charts/chart-2_animation.mp4'
import chart3Mp4 from '../../img/infrared/charts/chart-3_animation.mp4'
import infraRedWebm from '../../img/infrared/infrared_homepage_loop.webm'
import infraRedMp4 from '../../img/infrared/infrared_homepage_loop.mp4'
import infraRedPoster from '../../img/infrared/infrared_homepage_loop_poster.jpeg'
import infraBg from '../../img/infrared/infra.svg'
import redBg from '../../img/infrared/red.svg'

export const query = graphql`
  query InfraredReports {
    page: sanityPage(title: { eq: "Infrared Report" }) {
      title
      _rawBody
      metaTitle
      metaDescription
      metaKeywords
      metaImage {
        asset {
          _id
          url
        }
      }
      infrared {
        hero {
          backgroundVideo {
            asset {
              _id
              url
            }
          }
          videoPoster {
            asset {
              _id
              url
            }
          }
          headingImage {
            asset {
              _id
              url
            }
          }
        }
        secondSection {
          heading
          text
          downloadButtonText
          downloadButtonTextMobile
        }
        thirdSection {
          subheading
          heading
          text
        }
        fourthSection {
          subheading
          heading
          text
        }
        fifthSection {
          subheading
          heading
          text
        }
        currentReport {
          slug {
            current
          }
        }
        sixthSection {
          backgroundImage {
            asset {
              _id
              url
            }
          }
          heading
          text
          downloadButtonText
          downloadButtonTextMobile
          documents {
            year
            googleDriveLink
          }
        }
      }
    }
  }
`

const CloudReportPage = props => {
  const { data, errors } = props

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const dropdownRef = useRef()
  const { width } = useWindowSize()

  const chart1Ref = useRef()
  const chart2Ref = useRef()
  const chart3Ref = useRef()

  useEffect(() => {
    const videos = [chart1Ref.current, chart2Ref.current, chart3Ref.current].filter(Boolean)
    const observers = videos.map(video => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            video.play()
            observer.disconnect()
          }
        },
        { rootMargin: '-80px 0px', threshold: 0 }
      )
      observer.observe(video)
      return observer
    })
    return () => observers.forEach(o => o.disconnect())
  }, [])

  const reports =
    data.page?.infrared?.sixthSection?.documents.map(report => ({
      year: report.year,
      url: report.googleDriveLink
    })) || []

  const [selectedReport, setSelectedReport] = useState(reports[0]?.year ?? null)
  const [selectedReportUrl, setSelectedReportUrl] = useState()

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function previewLink(viewLink) {
    if (!viewLink) return null
    if (viewLink.includes('/view')) {
      return viewLink.replace('/view', '/preview')
    } else if (viewLink.includes('/preview')) {
      return viewLink + '/preview'
    }
  }

  function downloadLink(viewLink) {
    if (!viewLink) return null
    const fileIdMatch = viewLink.match(/\/d\/([a-zA-Z0-9_-]+)\//)
    if (fileIdMatch && fileIdMatch[1]) {
      const fileId = fileIdMatch[1]
      return `https://drive.google.com/uc?export=download&id=${fileId}`
    } else {
      return viewLink
    }
  }

  function trackDownload(report) {
    if (!report) return
    if (typeof gtag !== 'undefined') {
      gtag('event', `download_the_report_${report}`, {
        component: 'button'
      })
    }
  }

  function getFileUrlByYear() {
    const report = reports.find(doc => String(doc.year) === String(selectedReport))
    if (report) {
      setSelectedReportUrl(report.url || null)
    }
  }

  useEffect(() => {
    getFileUrlByYear()
  }, [selectedReport])

  if (errors) {
    return (
      <Layout>
        <GraphQLErrorList errors={errors} />
      </Layout>
    )
  }

  return (
    <Layout page="cloudReport" palette="white" hideContactBar version="1">
      <SEO
        title={data?.page?.metaTitle || data?.page?.title}
        description={data?.page?.metaDescription}
        keywords={data?.page?.metaKeywords ? data?.page?.metaKeywords.split(',') : []}
        imageFB={data?.page?.metaImage.asset.url}
      />

      <CloudIndexNav current="report" />

      <div className={styles.pageBackground}>
        <video
          className={styles.backgroundVideo}
          autoPlay
          muted
          loop
          playsInline
          poster={infraRedPoster}
          aria-hidden="true"
        >
          <source src={infraRedWebm} type="video/webm" />
          <source src={infraRedMp4} type="video/mp4" />
        </video>
        <img src={infraBg} className={styles.backgroundInfra} alt="" aria-hidden="true" />
        <img src={redBg} className={styles.backgroundRed} alt="" aria-hidden="true" />

      <InfraredReportHero
        video={data?.page?.infrared?.hero?.backgroundVideo}
        videoPoster={data?.page?.infrared?.hero?.videoPoster}
        noTransparent
      >
        <div>
          <img
            className={styles.reportHeroHeader}
            src={data?.page?.infrared?.hero?.headingImage.asset.url}
            alt="The InfraRed Report"
          />
        </div>
        <div>
          <section className={`${styles.secondSection}`}>
            <a
              href={data?.page?.infrared?.currentReport?.slug?.current
                ? `/reports/${data.page.infrared.currentReport.slug.current}/`
                : downloadLink(selectedReportUrl)}
              onClick={() => trackDownload(selectedReport)}
              rel="noopener noreferrer"
              target={data?.page?.infrared?.currentReport?.slug?.current ? '_self' : '_blank'}
              className={`${styles.downloadReportBtn}`}
            >
              <div className={styles.secondSectionWrapper}>
                <div className={styles.secondSectionText}>
                  {data?.page?.infrared?.secondSection?.heading && (
                    <h2>{data.page.infrared.secondSection.heading}</h2>
                  )}
                  {data?.page?.infrared?.secondSection?.text && (
                    <p>{data.page.infrared.secondSection.text}</p>
                  )}
                </div>
                <svg
                  width="75"
                  height="75"
                  viewBox="0 0 75 75"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_40_2133)">
                    <path
                      d="M37.5 74.2647C57.8046 74.2647 74.2647 57.8046 74.2647 37.5C74.2647 17.1954 57.8046 0.735291 37.5 0.735291C17.1954 0.735291 0.735291 17.1954 0.735291 37.5C0.735291 57.8046 17.1954 74.2647 37.5 74.2647Z"
                      fill="#8D1A1B"
                      stroke="#8D1A1B"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M55.8824 55.8824V19.1177H19.1177"
                      stroke="#F2F0E9"
                      strokeWidth="3"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M19.1177 55.8824L55.8824 19.1177"
                      stroke="#F2F0E9"
                      strokeWidth="3"
                      strokeMiterlimit="10"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_40_2133">
                      <rect width="75" height="75" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </a>
          </section>
        </div>
      </InfraredReportHero>

      <Container class={styles.container}>
        <section className={`${styles.thirdSection} ${styles.separator}`}>
          <div className={`${styles.primarySectionWrapper} ${styles.orderReversed} ${styles.wideMedia}`}>
            <div className={styles.primarySectionText}>
              {data?.page?.infrared?.thirdSection?.subheading && (
                <div className={styles.primarySectionSubtitle}>
                  {data.page.infrared.thirdSection.subheading}
                </div>
              )}
              {data?.page?.infrared?.thirdSection?.heading && (
                <h2>{data.page.infrared.thirdSection.heading}</h2>
              )}
              {data?.page?.infrared?.thirdSection?.text && (
                <p>{data.page.infrared.thirdSection.text}</p>
              )}
            </div>
            <div className={styles.thirdSectionImages}>
              <video
                ref={chart1Ref}
                className={styles.canvasCard}
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
              >
                <source src={chart1Mp4} type="video/mp4" />
              </video>
            </div>
          </div>
        </section>

        <section className={`${styles.fourthSection} ${styles.separator}`}>
          <div className={styles.fourthSectionWrapper}>
            <div className={`${styles.primarySectionText} ${styles.center}`}>
              {data?.page?.infrared?.fourthSection?.subheading && (
                <div className={styles.primarySectionSubtitle}>
                  {data.page.infrared.fourthSection.subheading}
                </div>
              )}
              {data?.page?.infrared?.fourthSection?.heading && (
                <h2>{data.page.infrared.fourthSection.heading}</h2>
              )}
              {data?.page?.infrared?.fourthSection?.text && (
                <p>{data.page.infrared.fourthSection.text}</p>
              )}
            </div>
            <video
              ref={chart2Ref}
              className={styles.fourthSectionImage}
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
            >
              <source src={chart2Mp4} type="video/mp4" />
            </video>
          </div>
        </section>

        <section className={`${styles.fifthSection} ${styles.separator}`}>
          <div className={`${styles.primarySectionWrapper} ${styles.wideMedia} ${styles.widerMedia}`}>
            <div className={styles.primarySectionText}>
              {data?.page?.infrared?.fifthSection?.subheading && (
                <div className={styles.primarySectionSubtitle}>
                  {data.page.infrared.fifthSection.subheading}
                </div>
              )}
              {data?.page?.infrared?.fifthSection?.heading && (
                <h2>{data.page.infrared.fifthSection.heading}</h2>
              )}
              {data?.page?.infrared?.fifthSection?.text && (
                <p>{data.page.infrared.fifthSection.text}</p>
              )}
            </div>
            <div>
              <video
                ref={chart3Ref}
                className={styles.fifthSectionImage}
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
              >
                <source src={chart3Mp4} type="video/mp4" />
              </video>
            </div>
          </div>
        </section>
      </Container>
      </div>
      <section className={`${styles.sixthSection}`} style={{ paddingTop: 0 }}>
        <div className={styles.sixthSectionInner}>
        <div
          className={styles.sixthSectionWrapper}
          style={{
            backgroundImage: data?.page?.infrared?.sixthSection?.backgroundImage?.asset?.url
              ? `url(${data?.page?.infrared?.sixthSection?.backgroundImage?.asset?.url})`
              : ''
          }}
        >
          <div className={styles.secondSectionButton}>
            {reports.length > 0 && (
              <div className={styles.reportFilter}>
                <div className={styles.filter}>
                  <div className={styles.label}>Filter</div>
                  <div className={styles.dropdown}>
                    <div ref={dropdownRef} className={styles.customSelect}>
                      <button
                        type="button"
                        className={`${styles.customSelectTrigger}${isFilterOpen ? ` ${styles.open}` : ''}`}
                        onClick={() => setIsFilterOpen(prev => !prev)}
                      >
                        {selectedReport}
                        <svg width="26" height="16" viewBox="0 0 26 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M24.709 1.05518L12.8895 13.002L1.06616 1.05518" stroke="#323131" strokeWidth="3" strokeMiterlimit="10"/>
                        </svg>
                      </button>
                      {isFilterOpen && (
                        <ul className={styles.customSelectList}>
                          {reports.map((report, index) => (
                            <li
                              key={index}
                              className={report.year === selectedReport ? styles.customSelectListItemActive : styles.customSelectListItem}
                              onClick={() => {
                                setSelectedReport(report.year)
                                setIsFilterOpen(false)
                              }}
                            >
                              {report.year}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.sixthSectionBtnFlex}>
            <div className={styles.secondSectionText}>
              {data?.page?.infrared?.sixthSection?.heading && (
                <h2>{data.page.infrared.sixthSection.heading}</h2>
              )}
              {data?.page?.infrared?.sixthSection?.text && (
                <p>{data.page.infrared.sixthSection.text}</p>
              )}
            </div>
            <a
              href={downloadLink(selectedReportUrl)}
              onClick={() => trackDownload(selectedReport)}
              style={{
                fontSize: 24
              }}
              rel="noopener noreferrer"
              target="_blank"
              className={`${styles.downloadReportBtn}`}
            >
              {width > 768
                ? data?.page?.infrared?.sixthSection?.downloadButtonText
                : data?.page?.infrared?.sixthSection?.downloadButtonTextMobile}
              <span>
                <svg
                  width="75"
                  height="75"
                  viewBox="0 0 75 75"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_40_2133)">
                    <path
                      d="M37.5 74.2647C57.8046 74.2647 74.2647 57.8046 74.2647 37.5C74.2647 17.1954 57.8046 0.735291 37.5 0.735291C17.1954 0.735291 0.735291 17.1954 0.735291 37.5C0.735291 57.8046 17.1954 74.2647 37.5 74.2647Z"
                      fill="#8D1A1B"
                      stroke="#8D1A1B"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M55.8824 55.8824V19.1177H19.1177"
                      stroke="#F2F0E9"
                      strokeWidth="3"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M19.1177 55.8824L55.8824 19.1177"
                      stroke="#F2F0E9"
                      strokeWidth="3"
                      strokeMiterlimit="10"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_40_2133">
                      <rect width="75" height="75" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </a>
          </div>
        </div>
        </div>
      </section>
    </Layout>
  )
}

export default CloudReportPage
